import { useEffect, useRef } from 'react'

// The sign-in background, after whitecircle.com's hero: stars on one ring far
// wider than the screen and centred below it, so only its top shows, as an
// arc. It turns slowly and the stars move out of the pointer's way. Plain
// WebGL2; without it the canvas stays black and sign-in works the same.

type Layer = {
  count: number // around the whole ring, on a 1440px wide screen
  spread: number // standard deviation off the ring, as a share of its radius
  size: [number, number] // css px
  alpha: number
  glow?: boolean
  pink?: boolean
}

const LAYERS: Layer[] = [
  { count: 90_000, spread: 0.03, size: [1, 2], alpha: 0.5 }, // the band
  { count: 40_000, spread: 0.07, size: [1, 1.6], alpha: 0.25 }, // dust
  { count: 16_000, spread: 0.006, size: [1, 2], alpha: 0.6 }, // a thin line
  { count: 3_000, spread: 0.05, size: [3, 10], alpha: 0.5, glow: true },
  {
    count: 600,
    spread: 0.045,
    size: [5, 12],
    alpha: 1,
    glow: true,
    pink: true,
  },
]
const WHITE = [1, 1, 1]
const PINK = [1, 0.447, 0.808] // whitecircle's #ff72ce

const VERTEX = `#version 300 es
layout(location = 0) in vec4 a_star; // angle, distance in radii, size, alpha
layout(location = 1) in float a_phase;
uniform vec2 u_resolution;
uniform vec2 u_center;
uniform float u_radius;
uniform float u_rotation;
uniform float u_pixelRatio;
uniform vec2 u_pointer;
uniform float u_push;
uniform float u_time;
uniform float u_pulse;
uniform vec2 u_hole;
uniform float u_collapse;
out float v_alpha;
out float v_size;

void main() {
  float angle = a_star.x + u_rotation;
  vec2 position = u_center + vec2(cos(angle), sin(angle)) * a_star.y * u_radius;
  vec2 away = position - u_pointer;
  float gap = length(away);
  float reach = 180.0 * u_pixelRatio;
  if (gap > 0.001 && gap < reach) {
    float falloff = 1.0 - gap / reach;
    position += away / gap * falloff * falloff * 40.0 * u_pixelRatio * u_push;
  }
  // The black hole: stars spiral into the hole, the nearest first, and go
  // dark at its edge.
  float fall = 0.0;
  if (u_collapse > 0.0) {
    vec2 fromHole = position - u_hole;
    float far = length(fromHole) / u_resolution.x;
    fall = clamp(u_collapse * 1.6 - far * 0.6, 0.0, 1.0);
    fall *= fall;
    float turned = atan(fromHole.y, fromHole.x) + fall * 7.0;
    position = u_hole + vec2(cos(turned), sin(turned)) * length(fromHole) * (1.0 - fall);
  }
  vec2 clip = position / u_resolution * 2.0 - 1.0;
  gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);
  v_size = a_star.z * u_pixelRatio * (1.0 + u_pulse * 0.3 * sin(u_time + a_phase)) * (1.0 - 0.6 * fall);
  gl_PointSize = v_size;
  v_alpha = a_star.w * (1.0 - smoothstep(0.85, 1.0, fall));
}
`

const FRAGMENT = `#version 300 es
precision mediump float;
in float v_alpha;
in float v_size;
uniform vec3 u_color;
uniform float u_glow;
out vec4 fragColor;

void main() {
  float d = length(gl_PointCoord - 0.5) * 2.0;
  if (d > 1.0) discard;
  float core = 1.0 - smoothstep(0.25, 0.25 + 2.0 / v_size, d);
  float halo = (1.0 - d) * (1.0 - d) * u_glow * 0.3;
  fragColor = vec4(u_color, max(core, halo) * v_alpha);
}
`

// Box-Muller: a normal sample, so stars thin out away from the ring.
const gaussian = () =>
  Math.sqrt(-2 * Math.log(1 - Math.random())) *
  Math.cos(2 * Math.PI * Math.random())

function stars(scale: number) {
  const counts = LAYERS.map((layer) => Math.round(layer.count * scale))
  const data = new Float32Array(counts.reduce((a, b) => a + b, 0) * 5)
  let i = 0
  LAYERS.forEach((layer, l) => {
    const [min, max] = layer.size
    for (let n = 0; n < counts[l]; n++) {
      data[i++] = Math.random() * Math.PI * 2
      data[i++] = 1 + gaussian() * layer.spread
      data[i++] = min + Math.random() * (max - min)
      data[i++] = layer.alpha * (0.6 + 0.4 * Math.random())
      data[i++] = Math.random() * Math.PI * 2
    }
  })
  return { data, counts }
}

function link(gl: WebGL2RenderingContext) {
  const program = gl.createProgram()
  for (const [type, source] of [
    [gl.VERTEX_SHADER, VERTEX],
    [gl.FRAGMENT_SHADER, FRAGMENT],
  ] as const) {
    const shader = gl.createShader(type)
    if (!shader) {
      return null
    }
    gl.shaderSource(shader, source)
    gl.compileShader(shader)
    gl.attachShader(program, shader)
  }
  gl.linkProgram(program)
  return gl.getProgramParameter(program, gl.LINK_STATUS) ? program : null
}

// How long the galaxy takes to fall into the hole once collapse is set.
const COLLAPSE_MS = 1500

export function Galaxy({ collapse = false }: { collapse?: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null)
  const collapsedAt = useRef<number | null>(null)

  useEffect(() => {
    collapsedAt.current = collapse ? performance.now() : null
  }, [collapse])

  useEffect(() => {
    const canvas = ref.current
    const gl = canvas?.getContext('webgl2', { alpha: false, antialias: false })
    const program = gl ? link(gl) : null
    if (!canvas || !gl || !program) {
      return
    }
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    // ponytail: stars and pixel ratio are fixed for the width the page opens
    // at; a resize only rescales the ring.
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2)
    const { data, counts } = stars(
      Math.min(Math.max(window.innerWidth / 1440, 0.5), 1.5),
    )

    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer())
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW)
    gl.enableVertexAttribArray(0)
    gl.vertexAttribPointer(0, 4, gl.FLOAT, false, 20, 0)
    gl.enableVertexAttribArray(1)
    gl.vertexAttribPointer(1, 1, gl.FLOAT, false, 20, 16)
    // biome-ignore lint/correctness/useHookAtTopLevel: WebGL, not a React hook
    gl.useProgram(program)
    gl.enable(gl.BLEND)
    gl.clearColor(0, 0, 0, 1)
    const at = (name: string) => gl.getUniformLocation(program, name)
    const u = {
      resolution: at('u_resolution'),
      center: at('u_center'),
      radius: at('u_radius'),
      rotation: at('u_rotation'),
      pixelRatio: at('u_pixelRatio'),
      pointer: at('u_pointer'),
      push: at('u_push'),
      time: at('u_time'),
      pulse: at('u_pulse'),
      color: at('u_color'),
      glow: at('u_glow'),
      hole: at('u_hole'),
      collapse: at('u_collapse'),
    }
    gl.uniform1f(u.pixelRatio, pixelRatio)

    // The pointer the stars avoid, eased towards where it really is.
    const pointer = { x: 0, y: 0, push: 0 }
    const target = { x: 0, y: 0, push: 0 }
    const start = performance.now()
    let last = start
    let rotation = 0
    let frame = 0

    function draw(now: number) {
      if (!canvas || !gl) {
        return
      }
      // Speeds up over five seconds, then turns about a degree a second.
      rotation +=
        ((now - last) / 1000) * 0.02 * Math.min((now - start) / 5000, 1)
      last = now
      const collapsing =
        collapsedAt.current === null
          ? 0
          : Math.max(0, Math.min((now - collapsedAt.current) / COLLAPSE_MS, 1))
      if (collapsing > 0) {
        target.push = 0
      }
      if (pointer.push < 0.01) {
        pointer.x = target.x
        pointer.y = target.y
      }
      pointer.x += (target.x - pointer.x) * 0.3
      pointer.y += (target.y - pointer.y) * 0.3
      pointer.push += (target.push - pointer.push) * 0.05

      const { width, height } = canvas
      const radius = 0.58 * width
      gl.uniform2f(u.resolution, width, height)
      gl.uniform2f(u.center, width / 2, height * 0.45 + radius)
      gl.uniform1f(u.radius, radius)
      gl.uniform1f(u.rotation, rotation)
      gl.uniform2f(u.pointer, pointer.x, pointer.y)
      gl.uniform1f(u.push, pointer.push)
      gl.uniform1f(u.time, now * 0.003)
      gl.uniform2f(u.hole, width / 2, height / 2)
      gl.uniform1f(u.collapse, collapsing)
      gl.clear(gl.COLOR_BUFFER_BIT)
      let first = 0
      LAYERS.forEach((layer, l) => {
        gl.uniform3fv(u.color, layer.pink ? PINK : WHITE)
        gl.uniform1f(u.glow, layer.glow ? 1 : 0)
        gl.uniform1f(u.pulse, layer.pink ? 1 : 0)
        // White adds up into a glow; pink is painted over it to stay pink.
        gl.blendFunc(gl.SRC_ALPHA, layer.pink ? gl.ONE_MINUS_SRC_ALPHA : gl.ONE)
        gl.drawArrays(gl.POINTS, first, counts[l])
        first += counts[l]
      })
      if (!still) {
        frame = requestAnimationFrame(draw)
      }
    }

    function resize() {
      if (!canvas || !gl) {
        return
      }
      canvas.width = canvas.clientWidth * pixelRatio
      canvas.height = canvas.clientHeight * pixelRatio
      gl.viewport(0, 0, canvas.width, canvas.height)
      if (still) {
        draw(performance.now())
      }
    }
    const move = (event: PointerEvent) => {
      target.x = event.clientX * pixelRatio
      target.y = event.clientY * pixelRatio
      // Only on the open sky: over the card the stars stay put.
      target.push = event.target === canvas ? 1 : 0
    }
    const leave = () => {
      target.push = 0
    }

    resize()
    window.addEventListener('resize', resize)
    if (!still) {
      window.addEventListener('pointermove', move)
      document.documentElement.addEventListener('pointerleave', leave)
      frame = requestAnimationFrame(draw)
    }
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('resize', resize)
      window.removeEventListener('pointermove', move)
      document.documentElement.removeEventListener('pointerleave', leave)
    }
  }, [])

  return (
    <canvas
      ref={ref}
      aria-hidden
      className="fade-in fixed inset-0 size-full animate-in duration-1000"
    />
  )
}
